import { PlateformeDto } from 'api/plateforme';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { Button } from 'components/ui/Button';
import { SearchIcon } from 'components/icons/Icons';
import { plateformesAPI } from 'services/swagger-api';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import PlateformeCard from './PlateformeCard';

const PlateformesCardList: React.FC = () => {
  const [plateformes, setPlateformes] = useState<PlateformeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Parcours multi-étapes en page dédiée (plateforme + PM corporate principale).
  const openAddForm = useCallback(() => {
    navigate('/back-office/plateformes/new');
  }, [navigate]);

  const fetchPlateformes = useCallback(async () => {
    try {
      const response = await plateformesAPI.getPlateformes();
      setPlateformes(response.plateformes);
    } catch (error) {
      console.error('Error fetching plateformes:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlateformes();
  }, [fetchPlateformes]);

  const handleDelete = useCallback(
    async (plateforme: PlateformeDto) => {
      const res = await plateformesAPI.deletePlateforme({ plateformeId: plateforme.id });
      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Échec du retrait de la plateforme.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        return;
      }
      toasterService.showToast({
        message: 'Plateforme retirée.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green,
      });
      await fetchPlateformes();
    },
    [fetchPlateformes],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return plateformes;
    return plateformes.filter((p) =>
      [p.gereeParNom, p.name, p.code, p.natureLabel]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [plateformes, search]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-sm text-gray-500">
          Une plateforme est identifiée par la personne morale qui la gère : c'est elle qui en
          assure la gouvernance.
        </p>
        <div className="flex items-center gap-2">
          {/* Même champ de recherche que la barre d'outils des tableaux génériques */}
          <div className="relative w-64">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder="Rechercher une PM ou un code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 text-sm leading-5 text-gray-900 placeholder-gray-500 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
            />
          </div>
          <Button color="primary" onClick={openAddForm}>
            <Plus data-slot="icon" />
            Ajouter une plateforme
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="h-[200px] animate-pulse rounded-xl bg-gray-200" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <p className="text-lg font-medium">
            {search ? 'Aucune plateforme ne correspond à la recherche' : 'Aucune plateforme'}
          </p>
          {!search && (
            <div className="mt-4">
              <Button color="primary" onClick={openAddForm}>
                <Plus data-slot="icon" />
                Ajouter une plateforme
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((plateforme) => (
            <PlateformeCard
              key={plateforme.id}
              plateforme={plateforme}
              onClick={() => navigate(`/back-office/plateformes/${plateforme.id}`)}
              onDelete={() => handleDelete(plateforme)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PlateformesCardList;
